import React, { useState } from 'react';
import { ChevronDown, ChevronUp, HelpCircle, BookOpen, CreditCard, Users, Clock, Award } from 'lucide-react';

const FAQPage = () => {
  const [openCategory, setOpenCategory] = useState(null);
  const [openQuestion, setOpenQuestion] = useState(null);

  const faqCategories = [
    {
      id: 'general',
      title: 'General Information',
      icon: HelpCircle,
      questions: [
        {
          q: 'What is Olewin Academy?',
          a: 'Olewin Academy is an IIT alumni-led educational platform offering comprehensive coaching for IIT-JEE, NEET, GATE, school exams, and professional certifications. We provide personal coaching, group training, mentorship, and skill development programs.'
        },
        {
          q: 'Who are the instructors at Olewin Academy?',
          a: 'Our faculty comprises IIT, AIIMS, and premier institution alumni with extensive teaching experience. All instructors have proven track records with students achieving top ranks in competitive examinations.'
        },
        {
          q: 'What is the teaching methodology?',
          a: 'We follow a student-centric approach combining conceptual clarity, problem-solving techniques, regular assessments, and personalized attention. Our curriculum is designed by IIT alumni and updated regularly to match current exam patterns.'
        },
        {
          q: 'Are classes conducted online or offline?',
          a: 'We offer both online and offline modes. Online classes are conducted via our interactive learning platform with live sessions, recorded lectures, and digital study materials. Offline classes are available at our centers in select locations.'
        },
        {
          q: 'What is the student-teacher ratio?',
          a: 'For group batches, we maintain 15-25 students per batch to ensure quality interaction. Personal coaching offers one-on-one sessions with dedicated mentors.'
        }
      ]
    },
    {
      id: 'enrollment',
      title: 'Enrollment & Admission',
      icon: Users,
      questions: [
        {
          q: 'How do I enroll in a course?',
          a: 'Visit our website, select your desired course, fill the enrollment form, and complete the payment process. You will receive login credentials and course access within 24 hours of successful payment.'
        },
        {
          q: 'What are the eligibility criteria?',
          a: 'Eligibility varies by course. Generally: Foundation courses (Class 6-10), JEE/NEET (Class 11-12), GATE/JAM (Graduates), Professional courses (as specified). Please check specific course requirements on the course page.'
        },
        {
          q: 'Is there an entrance test?',
          a: 'For advanced batches and scholarship programs, we conduct aptitude tests to assess current level and place students in appropriate batches. Foundation and beginner courses do not require entrance tests.'
        },
        {
          q: 'Can I enroll mid-session?',
          a: 'Yes, mid-session enrollment is allowed subject to seat availability. You will receive access to all previous recorded lectures and materials. The fee structure remains the same.'
        },
        {
          q: 'What documents are required for enrollment?',
          a: 'Required documents include: ID proof (Aadhar/Passport), latest academic records, passport-size photographs, and parent/guardian contact details for students below 18 years.'
        }
      ]
    },
    {
      id: 'fees',
      title: 'Fees & Payment',
      icon: CreditCard,
      questions: [
        {
          q: 'What are the course fees?',
          a: 'Course fees vary based on program, duration, and mode of instruction. Detailed fee structure is available on individual course pages. We offer flexible payment plans including one-time, quarterly, and monthly installments.'
        },
        {
          q: 'What payment methods are accepted?',
          a: 'We accept credit/debit cards, net banking, UPI, digital wallets, and bank transfers. EMI options are available for courses above ₹25,000 through selected banks and financial partners.'
        },
        {
          q: 'Is there a registration fee?',
          a: 'Yes, a one-time non-refundable registration fee of ₹500-₹2,000 (depending on the course) is charged at the time of enrollment. This covers administrative costs and study material.'
        },
        {
          q: 'Are there any hidden charges?',
          a: 'No. All fees are transparently displayed before enrollment. The course fee includes study materials, online resources, and regular assessments. Optional services like additional mock tests or personal mentorship sessions may have separate charges.'
        },
        {
          q: 'Do you offer scholarships?',
          a: 'Yes, we offer merit-based scholarships up to 50% for deserving students. Scholarship tests are conducted quarterly. Financial assistance is also available for economically disadvantaged students.'
        },
        {
          q: 'What is the refund policy?',
          a: 'Registration fees are non-refundable. Course fees are refundable within 7 days of enrollment with 20% deduction. After 7 days, no refunds are provided. See Terms of Service for complete refund policy details.'
        }
      ]
    },
    {
      id: 'courses',
      title: 'Courses & Content',
      icon: BookOpen,
      questions: [
        {
          q: 'What courses do you offer?',
          a: 'We offer: IIT-JEE (Main & Advanced), NEET, School Excellence (Class 6-12), IIT-JAM, GATE, Study Abroad counseling, Olympiad preparation, Professional certifications, and Skill development programs.'
        },
        {
          q: 'How long is the course duration?',
          a: 'Duration varies: Foundation courses (6-12 months), JEE/NEET (1-2 years), GATE/JAM (8-12 months), Professional certifications (3-8 months). Crash courses and revision programs have shorter durations.'
        },
        {
          q: 'Are study materials provided?',
          a: 'Yes, comprehensive study materials including theory notes, practice problems, previous year papers, and reference materials are provided in both digital and physical formats (where applicable).'
        },
        {
          q: 'Can I access recorded lectures?',
          a: 'Yes, all live sessions are recorded and available for viewing until course completion date. Recorded lectures can be accessed 24/7 through your student portal.'
        },
        {
          q: 'Are mock tests included?',
          a: 'Yes, regular mock tests are included in all competitive exam courses. Test frequency varies by course - typically weekly topic tests and monthly full-syllabus mocks with detailed analysis.'
        },
        {
          q: 'Can I switch between courses?',
          a: 'Course switching is allowed once within the first month of enrollment, subject to approval and availability. Price difference adjustment will be made accordingly.'
        }
      ]
    },
    {
      id: 'schedule',
      title: 'Schedule & Attendance',
      icon: Clock,
      questions: [
        {
          q: 'What are the class timings?',
          a: 'We offer multiple batch timings: Morning (6 AM - 9 AM), Day (10 AM - 2 PM), Evening (4 PM - 8 PM), and Weekend batches. Personal coaching offers flexible scheduling.'
        },
        {
          q: 'What if I miss a class?',
          a: 'All classes are recorded and available for 48 hours. You can watch the recording and submit assignments. For offline classes, you may attend the same topic in another batch (subject to availability).'
        },
        {
          q: 'Is attendance mandatory?',
          a: 'Yes, minimum 75% attendance is required to continue in the program and receive the completion certificate. Attendance below 75% may result in course termination without refund.'
        },
        {
          q: 'Can I take a break during the course?',
          a: 'Yes, you can request one course freeze for up to 30 days per year due to medical or emergency reasons. Proof documentation is required. Course access will be extended accordingly.'
        },
        {
          q: 'What happens if faculty is unavailable?',
          a: 'We maintain backup faculty for all subjects. In case of faculty unavailability, substitute teachers or rescheduled classes with the same faculty will be arranged.'
        }
      ]
    },
    {
      id: 'support',
      title: 'Support & Assistance',
      icon: Award,
      questions: [
        {
          q: 'How do I get my doubts cleared?',
          a: 'Multiple channels available: Live doubt-clearing sessions (scheduled weekly), online doubt forum (24-hour response), WhatsApp support groups, and one-on-one sessions with faculty (by appointment).'
        },
        {
          q: 'Is mentorship included in the course?',
          a: 'Basic mentorship including study planning and progress tracking is included in all courses. Advanced personal mentorship with IIT alumni is available as an add-on service.'
        },
        {
          q: 'Do you provide career counseling?',
          a: 'Yes, career counseling is provided for all students including college selection guidance, career path exploration, and interview preparation for study abroad aspirants.'
        },
        {
          q: 'What if I face technical issues?',
          a: 'Our technical support team is available during business hours (9 AM - 9 PM). Email support is available 24/7 with response within 12 hours. Emergency contact numbers provided for urgent issues.'
        },
        {
          q: 'Can parents track student progress?',
          a: 'Yes, parents receive monthly progress reports via email including attendance, test scores, areas of improvement, and faculty feedback. Parent-teacher meetings are conducted quarterly.'
        },
        {
          q: 'What happens after course completion?',
          a: 'You receive a completion certificate, access to alumni network, continued doubt support for 3 months, and priority enrollment for advanced courses at discounted rates.'
        }
      ]
    }
  ];

  const toggleCategory = (categoryId) => {
    setOpenCategory(openCategory === categoryId ? null : categoryId);
    setOpenQuestion(null);
  };

  const toggleQuestion = (categoryId, questionIndex) => {
    const key = `${categoryId}-${questionIndex}`;
    setOpenQuestion(openQuestion === key ? null : key);
  };

  return (
    <div style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif', backgroundColor: '#fff' }}>
      {/* Hero Section */}
      <section style={{
        background: 'linear-gradient(135deg, #000000 0%, #1a1a1a 100%)',
        color: 'white',
        padding: '80px 20px 60px',
        textAlign: 'center',
        marginTop:'15px'
      }}>
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          <HelpCircle size={60} color="#58A95B" style={{ marginBottom: '20px' }} />
          <h1 style={{
            fontSize: 'clamp(2rem, 5vw, 2.8rem)',
            fontWeight: 'bold',
            marginBottom: '1rem'
          }}>
            Frequently Asked Questions
          </h1>
          <p style={{ fontSize: '1.1rem', opacity: 0.9, lineHeight: '1.6' }}>
            Find answers to common questions about Olewin Academy's courses, enrollment, fees, and policies
          </p>
        </div>
      </section>

      {/* FAQ Content */}
      <section style={{ padding: '60px 20px', backgroundColor: '#f8f9fa' }}>
        <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {faqCategories.map((category) => {
              const IconComponent = category.icon;
              const isCategoryOpen = openCategory === category.id;
              
              return (
                <div key={category.id} style={{
                  backgroundColor: 'white',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
                  border: isCategoryOpen ? '2px solid #58A95B' : '2px solid transparent',
                  transition: 'all 0.3s ease'
                }}>
                  {/* Category Header */}
                  <div
                    onClick={() => toggleCategory(category.id)}
                    style={{
                      padding: '20px 25px',
                      background: isCategoryOpen ? 'linear-gradient(135deg, #58A95B 0%, #4a8f4d 100%)' : '#f8f9fa',
                      color: isCategoryOpen ? 'white' : '#000',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                      <IconComponent size={24} color={isCategoryOpen ? 'white' : '#58A95B'} />
                      <h3 style={{ margin: 0, fontSize: '1.3rem', fontWeight: '600' }}>
                        {category.title}
                      </h3>
                    </div>
                    {isCategoryOpen ? <ChevronUp size={24} /> : <ChevronDown size={24} />}
                  </div>

                  {/* Questions */}
                  {isCategoryOpen && (
                    <div style={{ padding: '10px' }}>
                      {category.questions.map((item, idx) => {
                        const isQuestionOpen = openQuestion === `${category.id}-${idx}`;
                        
                        return (
                          <div key={idx} style={{
                            borderBottom: idx < category.questions.length - 1 ? '1px solid #eee' : 'none',
                            margin: '10px 0'
                          }}>
                            <div
                              onClick={() => toggleQuestion(category.id, idx)}
                              style={{
                                padding: '15px 20px',
                                cursor: 'pointer',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                backgroundColor: isQuestionOpen ? '#f8f9fa' : 'transparent',
                                borderRadius: '8px',
                                transition: 'all 0.3s ease'
                              }}
                            >
                              <h6 style={{
                                margin: 0,
                                fontSize: '1.05rem',
                                fontWeight: '600',
                                color: isQuestionOpen ? '#58A95B' : '#333',
                                flex: 1
                              }}>
                                {item.q}
                              </h6>
                              {isQuestionOpen ? 
                                <ChevronUp size={20} color="#58A95B" /> : 
                                <ChevronDown size={20} color="#666" />
                              }
                            </div>
                            {isQuestionOpen && (
                              <div style={{
                                padding: '15px 20px 20px',
                                color: '#666',
                                lineHeight: '1.7',
                                fontSize: '0.98rem',
                                animation: 'fadeIn 0.3s ease'
                              }}>
                                {item.a}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Still Have Questions */}
      <section style={{
        padding: '60px 20px',
        background: 'linear-gradient(135deg, #58A95B 0%, #4a8f4d 100%)',
        color: 'white',
        textAlign: 'center'
      }}>
        <div style={{ maxWidth: '700px', margin: '0 auto' }}>
          <h2 style={{ fontSize: '2rem', fontWeight: 'bold', marginBottom: '1rem' }}>
            Still Have Questions?
          </h2>
          <p style={{ fontSize: '1.1rem', marginBottom: '2rem', lineHeight: '1.6' }}>
            Can't find what you're looking for? Our support team is here to help you.
          </p>
          <div style={{ display: 'flex', gap: '15px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button style={{
              backgroundColor: '#000000',
              border: 'none',
              padding: '12px 35px',
              fontSize: '1rem',
              fontWeight: '600',
              borderRadius: '8px',
              color: 'white',
              cursor: 'pointer',
              transition: 'all 0.3s ease'
            }}
            onMouseOver={(e) => e.target.style.backgroundColor = '#1a1a1a'}
            onMouseOut={(e) => e.target.style.backgroundColor = '#000000'}>
              Contact Support
            </button>
            <button style={{
              backgroundColor: 'transparent',
              border: '2px solid white',
              padding: '12px 35px',
              fontSize: '1rem',
              fontWeight: '600',
              borderRadius: '8px',
              color: 'white',
              cursor: 'pointer',
              transition: 'all 0.3s ease'
            }}
            onMouseOver={(e) => {
              e.target.style.backgroundColor = 'white';
              e.target.style.color = '#58A95B';
            }}
            onMouseOut={(e) => {
              e.target.style.backgroundColor = 'transparent';
              e.target.style.color = 'white';
            }}>
              Schedule a Call
            </button>
          </div>
        </div>
      </section>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        * {
          box-sizing: border-box;
        }

        button {
          font-family: inherit;
        }

        @media (max-width: 768px) {
          section {
            padding: 50px 15px !important;
          }
        }
      `}</style>
    </div>
  );
};

export default FAQPage;